import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Edit2, Save, X, CheckCircle2 } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("registrations");
  const [registrations, setRegistrations] = useState([]);
  const [judges, setJudges] = useState([]);
  const [hall, setHall] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("bb_admin_token")) {
      navigate("/admin");
      return;
    }
    api.get("/auth/me").catch(() => {
      localStorage.removeItem("bb_admin_token");
      navigate("/admin");
    });
    loadAll();
  }, [navigate]);

  const loadAll = async () => {
    try {
      const [r, j, h] = await Promise.all([
        api.get("/registrations"),
        api.get("/judges"),
        api.get("/hall-of-fame"),
      ]);
      setRegistrations(r.data);
      setJudges(j.data);
      setHall(h.data);
    } catch (e) {
      toast.error("Errore caricamento");
    }
  };

  const logout = () => {
    localStorage.removeItem("bb_admin_token");
    navigate("/admin");
  };

  const deleteJudge = async (id) => {
    if (!window.confirm("Eliminare questo giudice?")) return;
    await api.delete(`/judges/${id}`);
    toast.success("Eliminato");
    loadAll();
  };
  const deleteHall = async (id) => {
    if (!window.confirm("Eliminare questa entry?")) return;
    await api.delete(`/hall-of-fame/${id}`);
    toast.success("Eliminato");
    loadAll();
  };
  const deleteReg = async (id) => {
    if (!window.confirm("Eliminare questa iscrizione?")) return;
    await api.delete(`/registrations/${id}`);
    toast.success("Eliminato");
    loadAll();
  };
  const markPaid = async (id) => {
    await api.post(`/registrations/${id}/mark-paid`);
    toast.success("Pagamento confermato");
    loadAll();
  };

  return (
    <main className="min-h-screen px-6 md:px-12 py-10" data-testid="admin-dashboard">
      <div className="bb-container">
        <div className="flex items-center justify-between mb-10">
          <div>
            <span className="bb-eyebrow">Beer Battle</span>
            <h1 className="bb-display text-5xl mt-2">DASHBOARD</h1>
          </div>
          <button onClick={logout} className="bb-btn-outline flex items-center gap-2" data-testid="logout-btn">
            <LogOut size={16} /> Logout
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-zinc-800">
          {[
            { id: "registrations", label: `Iscrizioni (${registrations.length})` },
            { id: "judges", label: `Giuria (${judges.length})` },
            { id: "hall", label: `Hall of Fame (${hall.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm uppercase tracking-widest font-bold transition-all ${
                tab === t.id
                  ? "border-b-2 text-[var(--bb-primary)]"
                  : "text-zinc-500 hover:text-zinc-200"
              }`}
              style={tab === t.id ? { borderColor: "var(--bb-primary)" } : {}}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "registrations" && (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Birraio</th><th>Email</th><th>Birra</th><th>Stile</th><th>Pagamento</th><th>Data</th><th></th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r) => (
                  <tr key={r.id}>
                    <td className="font-semibold">{r.brewer_name}</td>
                    <td className="text-zinc-400 text-sm">{r.email}</td>
                    <td>{r.beer_name}</td>
                    <td className="text-zinc-500 text-sm">{r.beer_style}</td>
                    <td>
                      <span className={`text-xs uppercase tracking-widest font-bold ${
                        r.payment_status === "paid" ? "text-green-400" :
                        r.payment_status === "reported" ? "text-blue-400" :
                        r.payment_status === "pending" ? "text-yellow-400" : "text-red-400"
                      }`}>
                        {r.payment_status}
                      </span>
                    </td>
                    <td className="text-zinc-500 text-xs">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                    <td>
                      <div className="flex gap-3 items-center">
                        {r.payment_status !== "paid" && (
                          <button
                            onClick={() => markPaid(r.id)}
                            data-testid={`mark-paid-${r.id}`}
                            className="hover:text-green-300"
                            style={{ color: "var(--bb-primary)" }}
                            title="Conferma pagamento"
                          >
                            <CheckCircle2 size={16} />
                          </button>
                        )}
                        <button onClick={() => deleteReg(r.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {registrations.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-zinc-500 py-12">Nessuna iscrizione</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "judges" && <JudgesEditor judges={judges} reload={loadAll} onDelete={deleteJudge} />}
        {tab === "hall" && <HallEditor hall={hall} reload={loadAll} onDelete={deleteHall} />}
      </div>
    </main>
  );
}

function JudgesEditor({ judges, reload, onDelete }) {
  const empty = { name: "", role: "", bio: "", photo_url: "", year: new Date().getFullYear(), order: 0 };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/judges/${editingId}`, { ...form, year: Number(form.year), order: Number(form.order) });
        toast.success("Giudice aggiornato");
      } else {
        await api.post("/judges", { ...form, year: Number(form.year), order: Number(form.order) });
        toast.success("Giudice aggiunto");
      }
      setForm(empty);
      setEditingId(null);
      reload();
    } catch {
      toast.error("Errore");
    }
  };

  const startEdit = (j) => {
    setEditingId(j.id);
    setForm({ name: j.name, role: j.role, bio: j.bio, photo_url: j.photo_url || "", year: j.year, order: j.order });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="bb-card p-6 space-y-4" data-testid="judge-form">
        <h3 className="bb-display text-2xl">{editingId ? "Modifica giudice" : "Nuovo giudice"}</h3>
        {["name", "role", "photo_url"].map((f) => (
          <div key={f}>
            <label className="bb-label">{f === "name" ? "Nome" : f === "role" ? "Ruolo" : "Foto URL"}</label>
            <input
              data-testid={`judge-input-${f}`}
              required={f !== "photo_url"}
              value={form[f]}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              className="bb-input"
            />
          </div>
        ))}
        <div>
          <label className="bb-label">Bio</label>
          <textarea
            required
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className="bb-input min-h-[80px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="bb-label">Anno</label>
            <input type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bb-input" />
          </div>
          <div>
            <label className="bb-label">Ordine</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="bb-input" />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="bb-btn-primary flex-1 justify-center" data-testid="save-judge-btn">
            {editingId ? <><Save size={16} /> Salva</> : <><Plus size={16} /> Aggiungi</>}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(empty); }} className="bb-btn-outline">
              <X size={16} />
            </button>
          )}
        </div>
      </form>

      <div className="lg:col-span-2 space-y-3">
        {judges.map((j) => (
          <div key={j.id} className="bb-card p-4 flex items-center gap-4">
            {j.photo_url && <img src={j.photo_url} alt={j.name} className="w-14 h-14 object-cover" />}
            <div className="flex-1">
              <div className="font-semibold">{j.name}</div>
              <div className="text-xs uppercase tracking-widest" style={{ color: "var(--bb-primary)" }}>{j.role} · {j.year}</div>
              <div className="text-sm text-zinc-500 mt-1 line-clamp-1">{j.bio}</div>
            </div>
            <button onClick={() => startEdit(j)} className="text-zinc-400 hover:text-[var(--bb-primary)]"><Edit2 size={16} /></button>
            <button onClick={() => onDelete(j.id)} className="text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HallEditor({ hall, reload, onDelete }) {
  const empty = { year: new Date().getFullYear(), position: 1, brewer_name: "", beer_name: "", beer_style: "", score: "", is_live: false };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      year: Number(form.year),
      position: Number(form.position),
      score: form.score === "" ? null : Number(form.score),
    };
    try {
      if (editingId) {
        await api.put(`/hall-of-fame/${editingId}`, payload);
        toast.success("Aggiornato");
      } else {
        await api.post("/hall-of-fame", payload);
        toast.success("Aggiunto");
      }
      setForm(empty);
      setEditingId(null);
      reload();
    } catch {
      toast.error("Errore");
    }
  };

  const startEdit = (h) => {
    setEditingId(h.id);
    setForm({
      year: h.year, position: h.position, brewer_name: h.brewer_name,
      beer_name: h.beer_name, beer_style: h.beer_style || "",
      score: h.score ?? "", is_live: h.is_live,
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="bb-card p-6 space-y-4" data-testid="hall-form">
        <h3 className="bb-display text-2xl">{editingId ? "Modifica entry" : "Nuova entry"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="bb-label">Anno</label>
            <input type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bb-input" /></div>
          <div><label className="bb-label">Posizione</label>
            <input type="number" required value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="bb-input" /></div>
        </div>
        <div><label className="bb-label">Birraio</label>
          <input required value={form.brewer_name} onChange={(e) => setForm({ ...form, brewer_name: e.target.value })} className="bb-input" /></div>
        <div><label className="bb-label">Birra</label>
          <input required value={form.beer_name} onChange={(e) => setForm({ ...form, beer_name: e.target.value })} className="bb-input" /></div>
        <div><label className="bb-label">Stile</label>
          <input value={form.beer_style} onChange={(e) => setForm({ ...form, beer_style: e.target.value })} className="bb-input" /></div>
        <div><label className="bb-label">Punteggio</label>
          <input type="number" step="0.1" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} className="bb-input" /></div>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
          <input type="checkbox" checked={form.is_live} onChange={(e) => setForm({ ...form, is_live: e.target.checked })} />
          Risultato Live
        </label>
        <div className="flex gap-2">
          <button type="submit" className="bb-btn-primary flex-1 justify-center">
            {editingId ? <><Save size={16} /> Salva</> : <><Plus size={16} /> Aggiungi</>}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(empty); }} className="bb-btn-outline">
              <X size={16} />
            </button>
          )}
        </div>
      </form>

      <div className="lg:col-span-2 border border-zinc-800 overflow-x-auto" style={{ background: "#0F0F11" }}>
        <table className="bb-table">
          <thead><tr>
            <th>Anno</th><th>Pos.</th><th>Birraio</th><th>Birra</th><th>Punteggio</th><th></th>
          </tr></thead>
          <tbody>
            {hall.map((h) => (
              <tr key={h.id}>
                <td>{h.year}</td>
                <td className={`pos-${h.position}`}>{h.position}</td>
                <td>{h.brewer_name}</td>
                <td className="text-zinc-300">{h.beer_name}</td>
                <td>{h.score ?? "—"} {h.is_live && <span className="bb-tag-live ml-2">Live</span>}</td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(h)} className="text-zinc-400 hover:text-[var(--bb-primary)]"><Edit2 size={14} /></button>
                    <button onClick={() => onDelete(h.id)} className="text-red-400"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
